/**
 * Legal Calendar — mirrors CollectionsCalendar's month-grid + day-panel
 * layout but tracks legal milestones (PDN, DN, SD2, hearings, etc.)
 * instead of P2Ps.
 *
 * Same UX conventions:
 *   • Top header with filters (status, handler, type) + Add Event button
 *   • Month nav + KPI bar
 *   • Calendar grid (Mon-first); each day shows up to 4 events colour-coded
 *     by type tone; "+N more" overflow.
 *   • Right-rail day panel — click a day to drill in.
 *
 * Differences vs collections cal:
 *   • Items are event types not P2Ps. "Add event" supports auto-followups
 *     (e.g. SD2 served → server creates +18d and +21d items automatically).
 *   • Per-event actions: Complete, Re-diarise (mandatory reason), Cancel, Delete. "Missed" retired Sep 2026.
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { ukDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import {
  Calendar as CalIcon, ChevronLeft, ChevronRight, Loader2,
  Plus, CheckCircle2, Ban, Trash2, ExternalLink, Landmark, Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  listLegalEvents, listLegalEventTypes,
  updateLegalEvent, deleteLegalEvent, getAgeingHandlers, sortNames,
  getLegalDiarySettings,
} from "@/lib/api";
import AddLegalEventDialog from "./AddLegalEventDialog";
import RediariseDialog from "./RediariseDialog";
import LegalAgenda from "./LegalAgenda";
import { OwnerChip } from "./OwnerChip";

// Quick type groups (Emma #15) — matched on the event type key.
const TYPE_GROUPS = {
  hearings:  ["court_hearing", "bankruptcy_order", "award_sequestration", "charging_order_interim", "charging_order_final", "winding_up_petition"],
  deadlines: ["expires", "challenge_ends", "can_petition", "can_sequestrate", "petition_filed", "sequestration_applied"],
  chases:    ["chase_court", "chase_aib", "chase_process_server", "review", "follow"],
};
import { useAuth } from "@/contexts/AuthContext";
import { TONE_CLASS, STATUS_TONE, PriorityDot, PRIORITY_META, courtSummary } from "./legalPriority";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW    = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

export default function LegalCalendar() {
  const navigate = useNavigate();
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year,  setYear]  = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(null);

  const [events, setEvents]         = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [handlers, setHandlers]     = useState([]);
  const [diarySettings, setDiarySettings] = useState(null);
  const [loading, setLoading]       = useState(false);

  const [filter, setFilter] = useState({ status: "scheduled", handler: "all", type: "all" });
  const [addOpen, setAddOpen] = useState(false);
  // Dean, 8 Oct 2026 — Agenda is the default view; the month grid stays one click away
  const [view, setView] = useState(() => localStorage.getItem("legal-calendar-view") || "agenda");
  const switchView = (v) => { setView(v); localStorage.setItem("legal-calendar-view", v); };
  const { user: authUser } = useAuth();
  const myName = authUser?.name || "";
  const mineOnly = !!myName && filter.handler === myName;

  // One-time bootstrap — event types catalogue + handler list + diary settings
  useEffect(() => {
    (async () => {
      try {
        const [t, h, s] = await Promise.all([
          listLegalEventTypes(), getAgeingHandlers("legal"), getLegalDiarySettings(),
        ]);
        setEventTypes([...(t.types || [])].sort((a, b) => (a.label || "").localeCompare(b.label || "", "en", { sensitivity: "base" })));
        setHandlers(sortNames(h.handlers));
        setDiarySettings(s);
      } catch (e) {
        console.warn("bootstrap failed", e);
      }
    })();
  }, []);

  // Re-fetch when month / filters change (1mo window + 7-day buffer either side).
  // Agenda view: everything open from 2 years back (overdue tail) to 60 days ahead.
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const first = new Date(year, month, 1);
      const last  = new Date(year, month + 1, 0);
      const start = new Date(first); start.setDate(first.getDate() - 7);
      const end   = new Date(last);  end.setDate(last.getDate() + 7);
      if (view === "agenda") {
        const now = new Date();
        start.setTime(now.getTime()); start.setFullYear(now.getFullYear() - 2);
        end.setTime(now.getTime()); end.setDate(now.getDate() + 60);
      }
      const params = {
        from_date: isoDate(start),
        to_date:   isoDate(end),
        status:    filter.status  === "all" ? undefined : filter.status === "overdue" ? "scheduled" : filter.status,
        type:      filter.type === "all" || filter.type.startsWith("group:") ? undefined : filter.type,
        handler:   filter.handler === "all" ? undefined : filter.handler,
        include_notes: view === "agenda" ? false : undefined,
        limit: 2000,
      };
      const r = await listLegalEvents(params);
      let evs = r.events || [];
      if (filter.status === "overdue") {
        const todayKey = new Date().toISOString().slice(0, 10);
        evs = evs.filter((e) => e.status === "scheduled" && e.date && e.date < todayKey);
      }
      if (filter.type.startsWith("group:")) {
        const keys = TYPE_GROUPS[filter.type.slice(6)] || [];
        evs = evs.filter((e) => keys.some((k) => (e.type || "").includes(k)));
      }
      setEvents(evs);
    } catch (e) {
      toast.error("Calendar load failed: " + errorDetailMessage(e, e.message));
    } finally {
      setLoading(false);
    }
  }, [year, month, view, filter.status, filter.handler, filter.type]);
  useEffect(() => { reload(); }, [reload]);

  // Bucket by ISO date for fast grid rendering
  const byDate = useMemo(() => {
    const m = {};
    for (const ev of events) {
      const d = ev.date;
      if (!d) continue;
      (m[d] = m[d] || []).push(ev);
    }
    return m;
  }, [events]);

  // Grid generation — Monday-first
  const grid = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = (first.getDay() + 6) % 7;
    const daysIn = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysIn; d++) cells.push(new Date(year, month, d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month, year]);

  const totals = useMemo(() => {
    const t = { scheduled: 0, completed: 0, overdue: 0, cancelled: 0, red: 0, amber: 0, green: 0 };
    const todayKey = new Date().toISOString().slice(0, 10);
    for (const ev of events) {
      t[ev.status] = (t[ev.status] || 0) + 1;
      if (ev.status === "scheduled" && ev.date && ev.date < todayKey) t.overdue += 1;
      if (ev.priority && t[ev.priority] !== undefined) t[ev.priority] += 1;
    }
    return t;
  }, [events]);

  const goPrev = () => month === 0 ? (setMonth(11), setYear(year - 1)) : setMonth(month - 1);
  const goNext = () => month === 11 ? (setMonth(0),  setYear(year + 1)) : setMonth(month + 1);
  const goToday = () => { setMonth(today.getMonth()); setYear(today.getFullYear()); setSelectedDate(isoDate(today)); };

  const selectedEvents = selectedDate ? (byDate[selectedDate] || []) : [];

  return (
    <div className="space-y-4 p-4" data-testid="legal-calendar">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <CalIcon className="w-5 h-5 text-red-700" />
            <h1 className="text-xl font-semibold">Legal Calendar</h1>
          </div>
          <div className="flex items-center rounded-full border bg-slate-50 p-0.5" data-testid="lcal-view-toggle">
            {[["agenda", "Agenda"], ["month", "Month"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => switchView(v)}
                className={`h-7 px-3 rounded-full text-xs font-medium transition-colors ${view === v ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                aria-pressed={view === v}
                data-testid={`lcal-view-${v}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={filter.status} onValueChange={(v) => setFilter(f => ({ ...f, status: v }))}>
            <SelectTrigger className="h-8 w-[140px] text-xs" data-testid="lcal-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="scheduled">Open (hide completed)</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="all">All statuses</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center gap-1" data-testid="lcal-type-chips">
            {[["all", "All"], ["group:hearings", "Hearings"], ["group:deadlines", "Deadlines"], ["group:chases", "Chases"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(f => ({ ...f, type: v }))}
                className={`h-7 px-2 rounded-full border text-[11px] ${filter.type === v ? "bg-slate-900 text-white border-slate-900" : "bg-white hover:bg-slate-50"}`}
                data-testid={`lcal-chip-${v.replace("group:", "")}`}>{l}</button>
            ))}
          </div>
          <Select value={filter.type.startsWith("group:") ? "all" : filter.type} onValueChange={(v) => setFilter(f => ({ ...f, type: v }))}>
            <SelectTrigger className="h-8 w-[180px] text-xs" data-testid="lcal-filter-type">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              {eventTypes.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filter.handler} onValueChange={(v) => setFilter(f => ({ ...f, handler: v }))}>
            <SelectTrigger className="h-8 w-[160px] text-xs" data-testid="lcal-filter-handler">
              <SelectValue placeholder="Handler" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All handlers</SelectItem>
              <SelectItem value="Unassigned">Unassigned</SelectItem>
              {handlers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            size="sm"
            className="h-8 bg-red-700 hover:bg-red-800"
            onClick={() => setAddOpen(true)}
            data-testid="lcal-add-event"
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add event
          </Button>
        </div>
      </div>

      {/* Month nav + KPI bar */}
      <div className="flex items-center justify-between">
        {view === "month" ? (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={goPrev} data-testid="lcal-prev"><ChevronLeft className="w-4 h-4" /></Button>
            <h2 className="text-lg font-medium min-w-[170px] text-center" data-testid="lcal-month-label">
              {MONTHS[month]} {year}
            </h2>
            <Button variant="outline" size="sm" onClick={goNext} data-testid="lcal-next"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" onClick={goToday} className="text-xs" data-testid="lcal-today">Today</Button>
          </div>
        ) : (
          <div className="text-sm text-slate-600" data-testid="lcal-agenda-range">
            <span className="font-medium text-slate-900">Agenda</span> · overdue tail + next 60 days{mineOnly ? " · my actions" : ""}
          </div>
        )}
        <div className="flex items-center gap-3 text-xs">
          {/* Traffic-light priority — thresholds editable in Portal Settings → Legal Diary */}
          {(totals.red > 0 || totals.amber > 0 || totals.green > 0) && (
            <span className="flex items-center gap-2 pr-3 border-r" data-testid="lcal-priority-strip">
              <span className="flex items-center gap-1" title={PRIORITY_META.red.label}>
                <span className="w-2.5 h-2.5 rounded-full bg-red-600 inline-block" />
                <span className="font-semibold text-red-700">{totals.red}</span>
              </span>
              <span className="flex items-center gap-1" title={PRIORITY_META.amber.label}>
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400 inline-block" />
                <span className="font-semibold text-amber-700">{totals.amber}</span>
              </span>
              <span className="flex items-center gap-1" title={PRIORITY_META.green.label}>
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                <span className="font-semibold text-emerald-700">{totals.green}</span>
              </span>
            </span>
          )}
          <span><span className="font-semibold text-blue-700">{totals.scheduled}</span> scheduled</span>
          <span><span className="font-semibold text-emerald-700">{totals.completed}</span> completed</span>
          <span data-testid="lcal-overdue-total"><span className="font-semibold text-red-700">{totals.overdue}</span> overdue</span>
          {totals.cancelled > 0 && <span className="text-slate-500"><span className="font-semibold">{totals.cancelled}</span> cancelled</span>}
        </div>
      </div>

      {/* Body: agenda list OR month grid + right-rail panel */}
      {view === "agenda" ? (
        <>
          <LegalAgenda
            events={events}
            loading={loading}
            onOpenCase={(caseId) => navigate(`/legal/case/${caseId}?source=legal`)}
            onChanged={reload}
          />
          {loading && (
            <div className="text-center text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" /> Loading legal events…
            </div>
          )}
        </>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr,380px] gap-4">
        <div className="border rounded-lg overflow-hidden bg-white">
          <div className="grid grid-cols-7 bg-slate-50 border-b text-[10px] font-semibold uppercase tracking-wider text-slate-600">
            {DOW.map(d => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
          </div>
          <div className="grid grid-cols-7" data-testid="lcal-grid">
            {grid.map((d, i) => {
              if (!d) return <div key={`empty-${i}`} className="bg-slate-50/40 min-h-[110px] border-r border-b" />;
              const iso = isoDate(d);
              const list = byDate[iso] || [];
              const isToday = iso === isoDate(today);
              const isSelected = iso === selectedDate;
              const isPast = d < new Date(today.getFullYear(), today.getMonth(), today.getDate());
              return (
                <button
                  type="button"
                  key={iso}
                  onClick={() => setSelectedDate(iso)}
                  className={`relative min-h-[110px] border-r border-b p-1.5 text-left hover:bg-red-50/30 transition ${
                    isSelected ? "bg-red-100/40 ring-2 ring-inset ring-red-400" : ""
                  } ${isToday ? "bg-amber-50/40" : ""}`}
                  data-testid={`lcal-day-${iso}`}
                >
                  <div className={`text-xs font-semibold ${isPast ? "text-slate-400" : "text-slate-700"}`}>
                    {d.getDate()}
                    {isToday && <span className="ml-1 text-[9px] uppercase text-amber-700">Today</span>}
                  </div>
                  <div className="mt-1 space-y-0.5">
                    {list.slice(0, 4).map(ev => (
                      <div
                        key={ev.event_id}
                        className={`text-[10px] px-1.5 py-0.5 rounded border truncate flex items-center gap-1 ${TONE_CLASS[ev.tone] || TONE_CLASS.slate}`}
                        title={`${ev.label} · ${ev.customer_name || "—"}${ev.priority ? ` · ${PRIORITY_META[ev.priority].label}` : ""}`}
                      >
                        {ev.priority && (
                          <span data-testid={`grid-priority-dot-${ev.priority}`} className={`w-1.5 h-1.5 rounded-full shrink-0 ${PRIORITY_META[ev.priority].dot}`} />
                        )}
                        <span className="font-semibold truncate">{ev.label}</span>
                        {ev.customer_name && <span className="opacity-80 truncate">{shortName(ev.customer_name)}</span>}
                      </div>
                    ))}
                    {list.length > 4 && <div className="text-[10px] text-slate-500 font-medium">+{list.length - 4} more</div>}
                  </div>
                </button>
              );
            })}
          </div>
          {loading && (
            <div className="text-center text-xs text-muted-foreground py-2">
              <Loader2 className="w-3 h-3 animate-spin inline-block mr-1" /> Loading legal events…
            </div>
          )}
        </div>

        {/* Right-rail day panel */}
        <DayPanel
          isoDate={selectedDate}
          events={selectedEvents}
          onOpenCase={(caseId) => navigate(`/legal/case/${caseId}?source=legal`)}
          onChanged={reload}
        />
      </div>
      )}

      {/* Add event modal — shared dialog with diary caps + court attachment */}
      <AddLegalEventDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        eventTypes={eventTypes}
        defaultDate={selectedDate || isoDate(today)}
        diarySettings={diarySettings}
        onCreated={() => { setAddOpen(false); reload(); }}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function DayPanel({ isoDate: dateStr, events, onOpenCase, onChanged }) {
  if (!dateStr) {
    return (
      <div className="border rounded-lg bg-white p-6 text-center text-sm text-muted-foreground">
        Pick a day on the calendar to see legal events.
      </div>
    );
  }
  return (
    <div className="border rounded-lg bg-white flex flex-col max-h-[70vh]" data-testid="lcal-day-panel">
      <div className="border-b px-4 py-3">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{prettyDate(dateStr)}</div>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold">{events.length} event{events.length === 1 ? "" : "s"}</h3>
        </div>
      </div>
      <div className="overflow-auto flex-1">
        {events.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">No legal events on this day.</div>
        ) : (
          <ul className="divide-y">
            {events.map(ev => <EventRow key={ev.event_id} ev={ev} onOpenCase={onOpenCase} onChanged={onChanged} />)}
          </ul>
        )}
      </div>
    </div>
  );
}

function EventRow({ ev, onOpenCase, onChanged }) {
  const [busy, setBusy] = useState(false);
  const { isAdmin } = useAuth();
  const lockedForMe = !!ev.locked && !isAdmin();
  // Item 14: Legal-note events sourced from `legal_case_management.timeline[]`
  // are read-only here (source of truth is the LegalNotesCard on the case).
  const isLegalNote = ev.is_legal_note;
  const isOverdue = ev.status === "scheduled" && ev.date && ev.date < new Date().toISOString().slice(0, 10);
  const [rediarise, setRediarise] = useState(false);
  const setStatus = async (newStatus) => {
    try {
      setBusy(true);
      await updateLegalEvent(ev.event_id, { status: newStatus, actor_is_admin: isAdmin() });
      toast.success(`Marked ${newStatus}`);
      await onChanged?.();
    } catch (e) {
      toast.error("Update failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete "${ev.label}" on ${ukDate(ev.date)}? This cannot be undone.`)) return;
    try {
      setBusy(true);
      await deleteLegalEvent(ev.event_id, false, isAdmin());
      toast.success("Event deleted");
      await onChanged?.();
    } catch (e) {
      toast.error("Delete failed: " + errorDetailMessage(e, e.message));
    } finally { setBusy(false); }
  };
  return (
    <li className="p-3 hover:bg-slate-50" data-testid={`lcal-event-${ev.event_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <PriorityDot priority={ev.priority} daysOverdue={ev.days_overdue} />
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${TONE_CLASS[ev.tone] || TONE_CLASS.slate}`}>
              {ev.label}
            </span>
            <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${isOverdue ? "bg-red-50 text-red-700 border-red-300" : STATUS_TONE[ev.status]}`} data-testid={`lcal-status-${ev.event_id}`}>
              {isOverdue ? "overdue" : ev.status}{ev.rediarise_count ? ` · ↻×${ev.rediarise_count}` : ""}
            </span>
            {(ev.completed_at || ev.cancelled_at) && (
              <span className="text-[9px] text-slate-500" data-testid={`lcal-audit-stamp-${ev.event_id}`}>
                {new Date(ev.cancelled_at || ev.completed_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })} by {ev.cancelled_by || ev.completed_by || "—"}
              </span>
            )}
            {ev.priority && ev.days_overdue > 0 && (
              <span className="text-[9px] font-semibold text-red-700">{ev.days_overdue}d overdue</span>
            )}
            {isLegalNote && (
              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" title="Sourced from the case's legal notes timeline (read-only)">
                ✎ Legal note
              </span>
            )}
            {ev.locked && (
              <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-slate-100 text-slate-700 border-slate-300 inline-flex items-center gap-0.5"
                title="Hearing-prep task — locked to the hearing date. Complete it; only an admin can move or remove it.">
                <Lock className="w-2.5 h-2.5" /> locked
              </span>
            )}
            {ev.auto_followup_of && (
              <span className="text-[9px] text-slate-400" title="Auto-created follow-up">⤴︎ auto</span>
            )}
          </div>
          <div className="text-sm font-medium mt-0.5 truncate">{ev.customer_name || "—"}</div>
          <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-1.5 flex-wrap">{ev.loan_application_id || "—"} · <OwnerChip ev={ev} onChanged={onChanged} />{ev.legal_ongoing && <span className="ml-1 text-amber-700" title="Legal ongoing — account stays with Collections">⚖</span>}</div>
          {ev.court?.name && (
            <div className="text-[10px] text-slate-600 mt-0.5 flex items-center gap-1">
              <Landmark className="w-2.5 h-2.5 shrink-0" /> {courtSummary(ev.court)}
            </div>
          )}
          {ev.notes && <div className="text-[11px] text-slate-600 mt-1 italic">&ldquo;{ev.notes}&rdquo;</div>}
          {ev.schedule_override?.justification && (
            <div className="text-[10px] text-amber-800 mt-0.5" title="Scheduled beyond the diary limit">
              ⚠ Diary override: {ev.schedule_override.justification}
            </div>
          )}
        </div>
        {ev.case_exists === false ? (
          ev.source === "funding_debenture" ? (
            <a
              className="h-7 w-7 flex items-center justify-center text-emerald-700 hover:text-emerald-900"
              href={ev.companies_house_url || `https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(ev.customer_name || "")}`}
              target="_blank" rel="noreferrer"
              title={`Funded deal — open the company's charges register at Companies House${ev.mr01_deadline ? ` (MR01 deadline ${ev.mr01_deadline})` : ""}`}
              data-testid="legal-cal-ch-charges-link"
            >
              <Landmark className="w-3.5 h-3.5" />
            </a>
          ) : (
          <span
            className="h-7 w-7 flex items-center justify-center text-slate-300 cursor-not-allowed"
            title="No matching case on the portal — this is a legacy Airtable record with no portal case file to open"
            data-testid="legal-cal-no-case"
          >
            <ExternalLink className="w-3.5 h-3.5" />
          </span>
          )
        ) : (
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onOpenCase(ev.case_id)} title="Open case">
            <ExternalLink className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      {!isLegalNote && (
        <div className="flex items-center gap-1 mt-2">
          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus("completed")} disabled={busy || ev.status === "completed"}>
            <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-600" /> Complete
          </Button>
          {!lockedForMe && (
            <>
              {ev.status === "scheduled" && (
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setRediarise(true)} disabled={busy} data-testid={`lcal-rediarise-${ev.event_id}`}>
                  <CalIcon className="w-3 h-3 mr-1 text-sky-700" /> Re-diarise
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus("cancelled")} disabled={busy || ev.status === "cancelled" || !!ev.mandatory} title={ev.mandatory ? "Mandatory action — complete it instead" : undefined}>
                <Ban className="w-3 h-3 mr-1 text-slate-600" /> Cancel
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-[11px] text-rose-600 hover:text-rose-700 ml-auto" onClick={remove} disabled={busy}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </>
          )}
        </div>
      )}
      {rediarise && <RediariseDialog ev={ev} onClose={() => setRediarise(false)} onDone={async () => { setRediarise(false); await onChanged?.(); }} />}
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function isoDate(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}
function prettyDate(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
function shortName(name) {
  if (!name) return "";
  return name.length > 16 ? name.slice(0, 14) + "…" : name;
}
